import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Edit, CheckCircle, XCircle, Loader, Image as ImageIcon, Sparkles, FileArchive, FileText, Loader2 } from 'lucide-react';
import axios from 'axios';
import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import { getMediaUrl } from '../utils/mediaUrl';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const CarouselPreviewPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const [imagesFromDB, setImagesFromDB] = useState([]);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [userCredits, setUserCredits] = useState(null);

  const generationParams = location.state?.generationParams || {};
  const { 
    generatedImages: rawGeneratedImages = [], 
    platform = '', 
    imageStyles = [],
    totalProcessingTime = 0,
    topic = '',
    remaining_credits = null
  } = generationParams;
  
  // Fix URLs in generatedImages to use proper media URL (local or DigitalOcean Spaces)
  const generatedImages = rawGeneratedImages.map(img => {
    const fixedUrl = getMediaUrl(img.image_url);
    
    console.log('🔧 Fixing generation image URL:');
    console.log('   - Original:', img.image_url);
    console.log('   - Fixed:', fixedUrl);
    
    return {
      ...img,
      image_url: fixedUrl
    };
  });

  useEffect(() => {
    // Use remaining_credits from generation response if available
    if (remaining_credits !== null) {
      setUserCredits({
        available_credits: remaining_credits,
        free_credits: remaining_credits, // Simplified - backend should provide breakdown
        purchased_credits: 0
      });
      console.log('💳 Using credits from generation response:', remaining_credits);
      return;
    }

    // Fetch user's credit balance if not provided in generation response
    const fetchUserCredits = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.warn('⚠️ No auth token found, skipping credit fetch');
          return;
        }

        console.log('📞 Fetching user credits from:', `${API_BASE_URL}/ai-generation/credits/balance/`);
        
        const response = await axios.get(
          `${API_BASE_URL}/ai-generation/credits/balance/`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            }
          }
        );

        console.log('📊 Credit API Response:', response.data);

        if (response.data.success) {
          setUserCredits(response.data.balance);
          console.log('💳 User credits loaded:', response.data.balance);
        } else {
          console.warn('⚠️ Credit API returned success=false:', response.data);
        }
      } catch (error) {
        console.error('❌ Error fetching user credits:', error);
        console.error('   - Error details:', error.response?.data || error.message);
      }
    };

    // Fetch latest generated images from database
    const fetchImagesFromDB = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No auth token found');
          setLoading(false);
          return;
        }

        console.log('📥 Fetching generated images from database...');
        
        const response = await axios.get(
          `${API_BASE_URL}/generated-images/latest/?limit=${generatedImages.length || 10}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            }
          }
        );

        console.log('📊 API Response:', response.data);
        
        if (response.data.success && response.data.images.length > 0) {
          console.log('✅ Loaded images from database:', response.data.images);
          
          // Convert database format to match frontend format
          const formattedImages = response.data.images.map(img => {
            console.log('🖼️ Processing image:', img);
            console.log('   - Original URL:', img.image_url);
            
            // Fix URL using media utility
            const fullUrl = getMediaUrl(img.image_url);
            console.log('   - Full URL:', fullUrl);
            
            return {
              slide_number: img.slide_number,
              image_url: fullUrl,
              credits_used: 1, // 1 credit per image
              processing_time: img.processing_time,
              title: img.title,
              description: img.description,
              status: img.status
            };
          });
          
          console.log('📋 Formatted images:', formattedImages);
          setImagesFromDB(formattedImages);
        } else {
          console.warn('⚠️ No images found in database, using generation response');
          console.log('   - Response data:', response.data);
          console.log('   - Generation images:', generatedImages);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('❌ Error fetching images from database:', error);
        console.error('   - Error details:', error.response?.data || error.message);
        setLoading(false);
      }
    };

    // Only fetch credits if not provided in generation response
    if (remaining_credits === null) {
      fetchUserCredits();
    }

    if (generatedImages.length > 0) {
      console.log('🎬 Starting image fetch process...');
      console.log('   - Generated images count:', generatedImages.length);
      console.log('   - Generated images data:', generatedImages);
      
      // Wait a moment for images to be saved to DB, then fetch
      setTimeout(fetchImagesFromDB, 1000);
    } else {
      console.warn('⚠️ No generated images in state');
      setLoading(false);
    }
  }, [generatedImages, remaining_credits]);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDownloadMenu && !event.target.closest('.download-menu-container')) {
        setShowDownloadMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  const downloadAsZip = async () => {
    try {
      setDownloadingZip(true);
      setShowDownloadMenu(false);
      console.log('📦 Starting ZIP download...');

      const zip = new JSZip();
      const folder = zip.folder(topic || 'carousel');

      // Use whichever image set is available
      const imagesToDownload = imagesFromDB.length > 0 ? imagesFromDB : generatedImages;

      // Fetch all images and add to ZIP
      for (let i = 0; i < imagesToDownload.length; i++) {
        const image = imagesToDownload[i];
        if (!image.image_url) continue;

        console.log(`📥 Fetching slide ${i + 1}...`);
        
        const response = await fetch(image.image_url);
        const blob = await response.blob();
        
        const extension = image.image_url.split('.').pop()?.split('?')[0] || 'jpg';
        const filename = `slide-${i + 1}.${extension}`;
        
        folder.file(filename, blob);
      }

      console.log('🗜️ Generating ZIP file...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Download ZIP
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `${topic || 'carousel'}.zip`;
      link.click();
      
      console.log('✅ ZIP download complete!');
    } catch (error) {
      console.error('❌ ZIP download failed:', error);
      alert('Failed to download ZIP. Please try again.');
    } finally {
      setDownloadingZip(false);
    }
  };

  const downloadAsPdf = async () => {
    try {
      setDownloadingPdf(true);
      setShowDownloadMenu(false);
      console.log('📄 Starting PDF generation...');

      // Get platform dimensions
      const platformDimensions = {
        'instagram_square': { width: 1080, height: 1080 },
        'instagram_story': { width: 1080, height: 1920 },
        'linkedin_post': { width: 1200, height: 628 },
        'facebook_post': { width: 1200, height: 630 },
      };

      const dims = platformDimensions[platform] || { width: 1080, height: 1080 };
      
      // Create PDF with platform dimensions (convert px to mm)
      const pdfWidth = dims.width * 0.264583;
      const pdfHeight = dims.height * 0.264583;
      
      const pdf = new jsPDF({
        orientation: dims.width > dims.height ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pdfWidth, pdfHeight]
      });

      // Use whichever image set is available
      const imagesToDownload = imagesFromDB.length > 0 ? imagesFromDB : generatedImages;

      // Add each slide to PDF
      for (let i = 0; i < imagesToDownload.length; i++) {
        const image = imagesToDownload[i];
        if (!image.image_url) continue;

        console.log(`📥 Adding slide ${i + 1} to PDF...`);
        
        // Fetch image
        const response = await fetch(image.image_url);
        const blob = await response.blob();
        
        // Convert to base64
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

        // Add new page for each slide (except first)
        if (i > 0) {
          pdf.addPage([pdfWidth, pdfHeight]);
        }
        
        // Add image to PDF
        pdf.addImage(base64, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }

      console.log('💾 Saving PDF...');
      pdf.save(`${topic || 'carousel'}.pdf`);
      
      console.log('✅ PDF download complete!');
    } catch (error) {
      console.error('❌ PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Use images from database if available, otherwise use generation response
  const displayImages = imagesFromDB.length > 0 ? imagesFromDB : generatedImages;
  
  console.log('🎨 Display images:', displayImages);
  console.log('   - From DB:', imagesFromDB.length);
  console.log('   - From generation:', generatedImages.length);

  const handleBackToGenerator = () => {
    navigate('/create');
  };

  const handleEditInCanvas = () => {
    navigate('/editor', { state: { generationParams } });
  };

  const handleDownloadImage = (imageUrl, slideNumber) => {
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `carousel-slide-${slideNumber}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!displayImages || displayImages.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Images Generated</h2>
          <p className="text-gray-600 mb-6">Please generate images first.</p>
          <button
            onClick={handleBackToGenerator}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Go to Generator
          </button>
        </div>
      </div>
    );
  }

  const currentImage = displayImages[selectedSlide];
  
  // Debug current image
  console.log('🎬 Rendering preview page...');
  console.log('   - Current slide:', selectedSlide);
  console.log('   - Current image:', currentImage);
  console.log('   - Current image URL:', currentImage?.image_url);
  console.log('   - Display images count:', displayImages.length);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToGenerator}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                  <Sparkles className="h-6 w-6 text-purple-600" />
                  <span>Your AI-Generated Carousel</span>
                </h1>
                <p className="text-sm text-gray-600">{topic || 'Carousel Preview'}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Download Dropdown */}
              <div className="relative download-menu-container">
                <button
                  onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                  disabled={downloadingZip || downloadingPdf}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(downloadingZip || downloadingPdf) ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{downloadingZip ? 'Creating ZIP...' : 'Creating PDF...'}</span>
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      <span>Download All</span>
                    </>
                  )}
                </button>
                
                {/* Download Menu */}
                {showDownloadMenu && !downloadingZip && !downloadingPdf && (
                  <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                    <div className="py-1">
                      <button
                        onClick={downloadAsZip}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                      >
                        <FileArchive className="h-5 w-5 text-blue-600" />
                        <div>
                          <div className="font-medium text-gray-900">Download as ZIP</div>
                          <div className="text-xs text-gray-500">All slides in a zip file</div>
                        </div>
                      </button>
                      <button
                        onClick={downloadAsPdf}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors border-t border-gray-100"
                      >
                        <FileText className="h-5 w-5 text-red-600" />
                        <div>
                          <div className="font-medium text-gray-900">Download as PDF</div>
                          <div className="text-xs text-gray-500">Combined PDF document</div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleEditInCanvas}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
              >
                <Edit className="h-4 w-4" />
                <span>Edit in Canvas</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Preview */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Image Display */}
              <div className="relative bg-gray-900 aspect-square flex items-center justify-center">
                {loading ? (
                  <div className="text-center">
                    <Loader className="h-12 w-12 text-purple-500 animate-spin mx-auto mb-4" />
                    <p className="text-white">Loading image...</p>
                  </div>
                ) : currentImage?.image_url ? (
                  <img
                    src={currentImage.image_url}
                    alt={`Slide ${currentImage.slide_number}`}
                    className="max-w-full max-h-full object-contain"
                    onLoad={() => {
                      console.log('✅ Image loaded successfully:', currentImage.image_url);
                    }}
                    onError={(e) => {
                      console.error('❌ Image failed to load:', currentImage.image_url);
                      console.error('   - Current src:', e.target.src);
                      console.error('   - Image object:', currentImage);
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect fill="%23f3f4f6"/><text x="50%" y="50%" text-anchor="middle" fill="%236b7280" font-size="16">Image failed to load</text></svg>';
                    }}
                  />
                ) : (
                  <div className="text-center text-white">
                    <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
                    <p>Image generation failed</p>
                    <p className="text-sm text-gray-400 mt-2">{currentImage?.error}</p>
                  </div>
                )}

                {/* Slide Number Badge */}
                <div className="absolute top-4 left-4 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                  Slide {selectedSlide + 1} of {displayImages.length}
                </div>

                {/* Download Button */}
                {currentImage?.image_url && (
                  <button
                    onClick={() => handleDownloadImage(currentImage.image_url, currentImage.slide_number)}
                    className="absolute top-4 right-4 bg-black/70 hover:bg-black/90 text-white p-3 rounded-full transition-colors"
                  >
                    <Download className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* Image Details */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {currentImage?.title || `Slide ${currentImage?.slide_number}`}
                </h3>
                <p className="text-gray-600 mb-4">
                  {currentImage?.description}
                </p>

                {/* Image Stats */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-gray-500">Credits Used</p>
                    <p className="text-lg font-semibold text-purple-600">
                      {currentImage?.credits_used || 1} credit
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Processing Time</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {currentImage?.processing_time?.toFixed(1) || '0'}s
                    </p>
                  </div>
                </div>

                {/* Success/Error Indicator */}
                <div className="mt-4 flex items-center space-x-2">
                  {currentImage?.image_url ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-sm text-green-600 font-medium">Successfully Generated</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-red-500" />
                      <span className="text-sm text-red-600 font-medium">Generation Failed</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex justify-center space-x-4 mt-6">
              <button
                onClick={() => setSelectedSlide(Math.max(0, selectedSlide - 1))}
                disabled={selectedSlide === 0}
                className="px-6 py-3 bg-white rounded-lg shadow hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setSelectedSlide(Math.min(displayImages.length - 1, selectedSlide + 1))}
                disabled={selectedSlide === displayImages.length - 1}
                className="px-6 py-3 bg-white rounded-lg shadow hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Generation Summary */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Generation Summary</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Platform</p>
                  <p className="text-base font-medium text-gray-900 capitalize">
                    {platform.replace('-', ' ')}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Image Styles</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {imageStyles.map((style, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                        {style}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500">Credits Used</p>
                  <p className="text-2xl font-bold text-purple-600">{displayImages.length} credits</p>
                </div>

                {/* Remaining Credits Card */}
                {userCredits ? (
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-sm text-purple-700 font-medium">Remaining Credits</p>
                    <p className="text-3xl font-bold text-purple-600 mt-1">
                      {userCredits.available_credits}
                    </p>
                    <div className="flex items-center justify-between mt-2 text-xs text-purple-600">
                      <span>Free: {userCredits.free_credits}</span>
                      <span>Purchased: {userCredits.purchased_credits}</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm text-gray-500">Loading credits...</p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-500">Total Time</p>
                  <p className="text-2xl font-bold text-gray-900">{totalProcessingTime?.toFixed(1) || '0'}s</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Images Generated</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {displayImages.filter(img => img.image_url).length} / {displayImages.length}
                  </p>
                </div>
              </div>
            </div>

            {/* Thumbnails */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">All Slides</h3>
              
              <div className="grid grid-cols-2 gap-3">
                {displayImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedSlide(idx)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      selectedSlide === idx 
                        ? 'border-purple-600 shadow-lg' 
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    {img.image_url ? (
                      <img
                        src={img.image_url}
                        alt={`Slide ${img.slide_number}`}
                        className="w-full h-full object-cover"
                        onLoad={() => {
                          console.log(`✅ Thumbnail ${idx + 1} loaded:`, img.image_url);
                        }}
                        onError={(e) => {
                          console.error(`❌ Thumbnail ${idx + 1} failed:`, img.image_url);
                          console.error('   - Error event:', e);
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                    
                    {/* Slide Number */}
                    <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">
                      {idx + 1}
                    </div>
                    
                    {/* Status Indicator */}
                    <div className="absolute bottom-2 right-2">
                      {img.image_url ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarouselPreviewPage;
